import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import store, { AppDispatch, RootState } from "configureStore";
import { Button, Banner, TextField, Text, Checkbox, Select, Card, LegacyStack, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  setDestroy,
  setIsModalxOpen,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { post, requestRulesSignature, postTransportAdr, releaseTransport } from "services/orders.service";
import { fetchOrders } from "containers/orders/orders.slice";
import NightActionsCard from "containers/orders/night-actions/night-actions.card";

interface ConfirmModalProps {
  id: string;
  file: boolean;
  orderId: number;
  customer: string;
  date: string;
  title: string;
  nr: string;
  driver: string;
  transportCompany: string;
  terminal_number: string | null;
  type: string;
  is_adr?: boolean;
  is_signed?: boolean;
  terminal_status?: string | null;
  gate_exit_at?: string | null;
  night_mode?: boolean;
  night_ready?: boolean;
  night_step?: string | null;
  night_docs?: number;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  id,
  file,
  orderId,
  customer,
  date,
  title,
  nr,
  driver,
  transportCompany,
  terminal_number,
  type,
  is_adr = false,
  is_signed = false,
  terminal_status = null,
  gate_exit_at = null,
  night_mode = false,
  night_ready = false,
  night_step = null,
  night_docs = 0,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [terminalNumber, setTerminalNumber] = useState("");
  const [isAdrChecked, setIsAdrChecked] = useState(is_adr);
  const [adrLoading, setAdrLoading] = useState(false);
  const [adrError, setAdrError] = useState<string | null>(null);
  const [signatureLanguage, setSignatureLanguage] = useState("LT");
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [signatureSuccess, setSignatureSuccess] = useState(false);

  // Get orders data to find the latest record after update
  const {
    ordersReducer: { ordersCollection },
  } = useSelector((state: RootState) => state);

  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [releaseDone, setReleaseDone] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // one unified loading state: null, "save", "auto", "remove"
  const [loadingButton, setLoadingButton] = useState<
    null | "save" | "auto" | "remove"
  >(null);

  // Handle ADR toggle
  const handleAdrToggle = async (checked: boolean) => {
    try {
      setAdrLoading(true);
      setAdrError(null);
      
      const formData = new FormData();
      formData.append("type", type);
      formData.append("id_transport", id);
      formData.append("is_adr", checked.toString());
      
      await postTransportAdr(formData);
      setIsAdrChecked(checked);
      
      // Refresh orders data to get updated ADR status
      await dispatch(fetchOrders());
      
    } catch (error: any) {
      setAdrError(error.message || "Failed to update ADR status");
    } finally {
      setAdrLoading(false);
    }
  };

  // 🔹 Auto assign terminal
  const handleAutoRequest = async (idTransport: string) => {
    try {
      setLoadingButton("auto");
      const formData = new FormData();
      formData.append("id_transport", idTransport);
      formData.append("terminal_order", "auto");
      formData.append("type", type);
      await post(formData);

      // Fetch fresh data and wait for it to complete
      await dispatch(fetchOrders());
      
      // Get the fresh data from the store after fetch
      const currentState = store.getState();
      const freshOrdersCollection = currentState.ordersReducer.ordersCollection;
      
      const updatedItem = freshOrdersCollection.find(
        (order: any) => order.id === idTransport && order.type === type
      );
      if (updatedItem) {
        dispatch(setModalxComponentProps(updatedItem));
        setTerminalNumber((updatedItem as any).terminal_number || "");
      }

      setSuccessMessage("ok");
    } catch (e) {
      setErrMsg("please_try_again");
    } finally {
      setLoadingButton(null);
    }
  };

  // 🔹 Save or remove terminal number
  const handleSaveRequest = async (
    idTransport: string,
    terminalOrder: string,
    mode: "save" | "remove"
  ) => {
    try {
      setLoadingButton(mode);
      const formData = new FormData();
      formData.append("id_transport", idTransport);
      formData.append("terminal_order", terminalOrder); // Pass directly
      formData.append("type", type);
      await post(formData);

      // Fetch fresh data and wait for it to complete
      await dispatch(fetchOrders());
      
      // Get the fresh data from the store after fetch
      const currentState = store.getState();
      const freshOrdersCollection = currentState.ordersReducer.ordersCollection;
      
      const updatedItem = freshOrdersCollection.find(
        (order: any) => order.id === idTransport && order.type === type
      );
      if (updatedItem) {
        dispatch(setModalxComponentProps(updatedItem));
        setTerminalNumber((updatedItem as any).terminal_number || "");
      }

      setSuccessMessage("ok");
    } catch (e) {
      setErrMsg("please_try_again");
      setLoadingButton(null);
    } finally {
      setLoadingButton(null);
    }
  };

  // Step 13: the dispatcher has released the truck, the guard opens the barrier.
  // Only offered once accounting is done with it, and only once.
  const canRelease = terminal_status === "ready_to_leave" && !gate_exit_at && !releaseDone;

  const handleRelease = async () => {
    setReleaseError(null);
    setReleaseLoading(true);
    try {
      const formData = new FormData();
      formData.append("id_transport", id);
      formData.append("type", type);

      const res = await releaseTransport(formData);
      if (res?.success === false) {
        setReleaseError(
          res?.error === "not_ready_to_leave"
            ? t("not_ready_to_leave")
            : res?.error || t("please_try_again")
        );
        setReleaseLoading(false);
        return;
      }
      setReleaseDone(true);
      dispatch(fetchOrders());
    } catch (e) {
      setReleaseError(t("please_try_again"));
    } finally {
      setReleaseLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  // Same refresh-then-reinject pattern as handleSaveRequest/handleAutoRequest,
  // so the night-actions card advances to the next step without the modal closing.
  const handleNightRefresh = async () => {
    await dispatch(fetchOrders());
    const currentState = store.getState();
    const freshOrdersCollection = currentState.ordersReducer.ordersCollection;
    const updatedItem = freshOrdersCollection.find(
      (order: any) => order.id === id && order.type === type
    );
    if (updatedItem) {
      dispatch(setModalxComponentProps(updatedItem));
    }
  };

  // Handle signature request
  const handleSignatureRequest = async () => {
    setSignatureLoading(true);
    setSignatureError(null);
    setSignatureSuccess(false);

    try {
      // The direction MUST travel with the id: id_unload_transport and
      // id_upload_transport are separate sequences, so without it the backend
      // reads the upload table by an unload id and puts an unrelated driver's
      // name on the document sent for signature.
      const result = await requestRulesSignature(id, signatureLanguage, type);

      if (result && (result.success === true || !result.error)) {
        // Refresh orders data
        await dispatch(fetchOrders());
        setSignatureSuccess(true);
      } else {
        setSignatureError(result?.error || "Failed to send signature request");
      }
    } catch (err: any) {
      setSignatureError(err.message || "Failed to send signature request");
    } finally {
      setSignatureLoading(false);
    }
  };

  const languageOptions = [
    { label: "Lietuvių", value: "LT" },
    { label: "English", value: "EN" },
    { label: "Русский", value: "RU" },
    { label: "Polski", value: "PL" },
  ];

  // ---- Nodes for display ----
  const dateNode = date ? (
    <Text as="p" variant="bodyMd">
      {t("date")}: {date}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_date_provided")}
    </Text>
  );

  const orderNumberNode = orderId ? (
    <Text as="p" variant="bodyMd">
      {t("order_number")}: {orderId}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_order_provided")}
    </Text>
  );

  const transportCompanyNode = transportCompany ? (
    <Text as="p" variant="bodyMd">
      {t("transport_company")}: {transportCompany}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_transport_company_provided")}
    </Text>
  );

  const truckPlateNumberNode = nr ? (
    <Text as="p" variant="bodyMd">
      {t("truck_plate_number")}: {nr}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_truck_plate_number_provided")}
    </Text>
  );

  const driverNameNode = driver ? (
    <Text as="p" variant="bodyMd">
      {t("driver_name")}: {driver}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_driver_name_provided")}
    </Text>
  );

  const terminalNumberNode = terminal_number ? (
    <Text as="p" variant="bodyMd">
      {t("terminal_number")}: {terminal_number}
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_terminal_number_provided")}
    </Text>
  );

  // ---- Render ----
  return (
    <LegacyStack vertical spacing="loose">
      {/* Order Information Card */}
      <Card>
        <LegacyStack vertical spacing="tight">
          <Text as="h3" variant="headingMd">
            {t("order_information")}
          </Text>
          <LegacyStack vertical spacing="extraTight">
            {dateNode}
            {orderNumberNode}
            {transportCompanyNode}
            {truckPlateNumberNode}
            {driverNameNode}
            {terminalNumberNode}
          </LegacyStack>
        </LegacyStack>
      </Card>

      {/* Terminal Assignment Card */}
      <Card>
        <LegacyStack vertical spacing="tight">
          <Text as="h3" variant="headingMd">
            {t("terminal_assignment")}
          </Text>
          
          {/* Terminal Assignment Messages */}
          {(errMsg || successMessage) && (
            <LegacyStack vertical spacing="tight">
              {errMsg && (
                <Banner tone="critical">{t(errMsg)}</Banner>
              )}
              {successMessage && (
                <Banner tone="info">{t(successMessage)}</Banner>
              )}
            </LegacyStack>
          )}
          
          <LegacyStack vertical>
            {/* Terminal Number Input */}
            <LegacyStack vertical spacing="extraTight">
              <TextField
                label="terminalNumber"
                labelHidden
                id="terminalNumber"
                name="terminalNumber"
                type="text"
                autoComplete="off"
                value={terminalNumber}
                onChange={(value) => {
                  if (/^\d*$/.test(value)) {
                    setTerminalNumber(value);
                  }
                }}
                placeholder={t("enter_terminal_number")}
              />
            </LegacyStack>


            {/* Action Buttons */}
            <LegacyStack spacing="tight">
              {terminalNumber !== "" && (
                <Button
                  onClick={() => handleSaveRequest(id, terminalNumber, "save")}
                  loading={loadingButton === "save"}
                  variant="primary"
                >
                  {t("save")}
                </Button>
              )}
              {!terminal_number && (
                <Button
                  onClick={() => handleAutoRequest(id)}
                  loading={loadingButton === "auto"}
                >
                  {t("auto")}
                </Button>
              )}
              {terminal_number && (
                <Button
                  onClick={() => handleSaveRequest(id, "", "remove")}
                  loading={loadingButton === "remove"}
                  tone="critical"
                >
                  {t("remove")}
                </Button>
              )}
            </LegacyStack>
          </LegacyStack>
        </LegacyStack>
      </Card>

      {/* ADR Control Card */}
      <Card>
        <LegacyStack vertical spacing="tight">
          <LegacyStack alignment="center" spacing="tight">
            <Text as="h3" variant="headingMd">
              {t("ADR")}
            </Text>
            {adrLoading && (
              <Spinner size="small" />
            )}
          </LegacyStack>
          
          {/* ADR Messages */}
          {adrError && (
            <Banner tone="critical" onDismiss={() => setAdrError(null)}>
              {adrError}
            </Banner>
          )}
          
          <LegacyStack vertical>
            <Checkbox
              label={t("ADR")}
              checked={isAdrChecked}
              onChange={handleAdrToggle}
              disabled={adrLoading}
            />
          </LegacyStack>
        </LegacyStack>
      </Card>

      {/* Signature Request Card - Only show if not signed */}
      {!is_signed && (
        <Card>
          <LegacyStack vertical spacing="tight">
            <Text as="h3" variant="headingMd">
              {t("sign_rules")}
            </Text>
            
            {/* Signature Messages */}
            {(signatureError || signatureSuccess) && (
              <LegacyStack vertical spacing="tight">
                {signatureError && (
                  <Banner tone="critical" onDismiss={() => setSignatureError(null)}>
                    {signatureError}
                  </Banner>
                )}
                {signatureSuccess && (
                  <Banner tone="success">
                    {t("request_signature")} - {t("sent")}!
                  </Banner>
                )}
              </LegacyStack>
            )}
            
            <LegacyStack vertical>
              {/* Language Selection */}
              <LegacyStack vertical spacing="extraTight">
                <Select
                  label="language"
                  labelHidden
                  options={languageOptions}
                  value={signatureLanguage}
                  onChange={setSignatureLanguage}
                  disabled={signatureLoading || signatureSuccess}
                />
              </LegacyStack>

              {/* Signature Request Button */}
              <Button
                variant="primary"
                loading={signatureLoading}
                onClick={handleSignatureRequest}
                disabled={signatureLoading || signatureSuccess}
              >
                {t("request_signature")}
              </Button>
            </LegacyStack>
          </LegacyStack>
        </Card>
      )}

      <NightActionsCard
        id={id}
        type={type}
        night_mode={night_mode}
        night_ready={night_ready}
        night_step={night_step}
        night_docs={night_docs}
        gate_exit_at={gate_exit_at}
        onRefresh={handleNightRefresh}
      />

      {canRelease && (
        <Card>
          <LegacyStack vertical spacing="tight">
            {releaseError && <Banner tone="critical">{releaseError}</Banner>}
            <Button
              variant="primary"
              tone="success"
              size="large"
              loading={releaseLoading}
              disabled={releaseLoading}
              onClick={handleRelease}
            >
              {t("release_truck")}
            </Button>
          </LegacyStack>
        </Card>
      )}

      {releaseDone && <Banner tone="success">{t("truck_released")}</Banner>}
    </LegacyStack>
  );
};

export default ConfirmModal;
